import React, { useState, useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Button, Card, CardBody, CardFooter, CardHeader } from 'reactstrap';
import { createSelector } from 'reselect';
import { useDispatch, useSelector } from 'react-redux';
import { constants } from '../../../../../config';
import { getConcession, getRisksMaster } from '../../../../../slices/thunks';

mapboxgl.accessToken = constants.REACT_APP_MAPBOX;

const MapWithLayers = ({ title, code_siep, height, lat, lon, code }) => {
    const dispatch = useDispatch();
    const mapContainer = useRef(null);
    const mapInstance = useRef(null);

    // Estados para mostrar u ocultar capas
    const [showBarrio, setShowBarrio] = useState(true);
    const [showConcession, setShowConcession] = useState(true);

    const selectorConcession = createSelector(
        state => state.DataSped,
        data => ({ itemConcession: data.itemConcession, itemRisksMaster: data.itemRisksMaster })
    );
    const { itemConcession, itemRisksMaster } = useSelector(selectorConcession);

    useEffect(() => {
        dispatch(getConcession(code_siep));
        dispatch(getRisksMaster());
    }, [code_siep]);

    // Selección de tema de mapa
    const selectLayoutState = (state) => state.Layout;
    const selectLayoutProperties = createSelector(
        selectLayoutState,
        (layout) => ({ layoutModeType: layout.layoutModeType })
    );
    const { layoutModeType } = useSelector(selectLayoutProperties);

    const mapStyle = layoutModeType === 'dark'
        ? 'mapbox://styles/mapbox/dark-v10'
        : 'mapbox://styles/mapbox/streets-v12';

    useEffect(() => {
        if (!mapContainer.current || lat == null || lon == null) return;

        const centerCoordinates = [lon, lat];

        if (mapInstance.current) {
            mapInstance.current.remove();
        }

        mapInstance.current = new mapboxgl.Map({
            container: mapContainer.current,
            style: mapStyle,
            center: centerCoordinates,
            zoom: 14,
        });

        mapInstance.current.on('load', () => {

            const markerElement = document.createElement('div');
            markerElement.style.width = '5px';
            markerElement.style.height = '5px';
            markerElement.style.borderRadius = '50%';
            markerElement.style.backgroundColor = "var(--vz-primary)",
                markerElement.style.boxShadow = '0 0 2px rgba(0,0,0,0.5)';

            new mapboxgl.Marker({ element: markerElement })
                .setLngLat(centerCoordinates)
                .addTo(mapInstance.current);



            // // Añadir marcador en el centro de la concesión
            // new mapboxgl.Marker()
            //     .setLngLat(centerCoordinates)
            //     .addTo(mapInstance.current);



            // Agregar polígonos si itemConcession existe
            if (itemConcession) {

                if (itemConcession.intesal_polygon) {
                    const invertedCoordinates2 = itemConcession.intesal_polygon.map(coord => [coord[1], coord[0]]);
                    const invertedGeometry2 = {
                        type: "Polygon",
                        coordinates: [invertedCoordinates2]
                    };
                    addPolygon(invertedGeometry2, itemConcession.color_riesgo, 'Intesal');
                }

                if (itemConcession.geometry) {
                    const invertedCoordinates = itemConcession.geometry.coordinates.map(coord => [coord[1], coord[0]]);
                    const invertedGeometry = {
                        ...itemConcession.geometry,
                        coordinates: invertedCoordinates
                    };
                    addPolygon(invertedGeometry, '#007cbf', 'Concesión');
                }
            }

        });

        return () => {
            if (mapInstance.current) {
                mapInstance.current.remove();
                mapInstance.current = null;
            }
        };
    }, [lat, lon, mapStyle, itemConcession]);

    const addPolygon = (geometry, color, name) => {
        if (!mapInstance.current || !geometry || !geometry.coordinates) return;

        let formattedCoordinates;
        if (geometry.type === "Polygon") {
            formattedCoordinates = Array.isArray(geometry.coordinates[0][0])
                ? geometry.coordinates
                : [geometry.coordinates];
        } else {
            console.error(`Tipo de geometría no soportado: ${geometry.type}`);
            return;
        }

        if (mapInstance.current.getLayer(name)) {
            mapInstance.current.removeLayer(name);
            mapInstance.current.removeSource(name);
        }

        mapInstance.current.addSource(name, {
            type: 'geojson',
            data: {
                type: 'Feature',
                geometry: {
                    type: 'Polygon',
                    coordinates: formattedCoordinates,
                },
            },
        });

        mapInstance.current.addLayer({
            id: name,
            type: 'fill',
            source: name,
            layout: {},
            paint: {
                'fill-color': color,
                'fill-opacity': 0.3,
            },
        });

        mapInstance.current.addLayer({
            id: `${name}-border`,
            type: 'line',
            source: name,
            layout: {},
            paint: {
                'line-color': color,
                'line-width': 0.8, // Reducido para adelgazar el borde
            },
        });
    };





    const getBoundingBox = (geometry) => {
        if (!geometry || !geometry.coordinates) return null;

        let coords = [];

        if (geometry.type === "Polygon") {
            coords = geometry.coordinates[0]; // Tomamos el anillo exterior
        } else if (geometry.type === "MultiPolygon") {
            coords = geometry.coordinates.flat(2); // Aplanamos para obtener solo las coordenadas
        } else {
            console.warn("Tipo de geometría no soportado:", geometry.type);
            return null;
        }

        // Convertir y filtrar coordenadas
        const validCoords = coords
            .map(coord => [parseFloat(coord[1]), parseFloat(coord[0])]) // Invertimos lat/lon a lon/lat
            .filter(coord => !isNaN(coord[0]) && !isNaN(coord[1]));

        if (validCoords.length < 2) {
            //console.warn("No hay suficientes coordenadas válidas para calcular el bounding box.");
            return null;
        }

        // Calcular los extremos del bounding box
        const minLng = Math.min(...validCoords.map(c => c[0]));
        const minLat = Math.min(...validCoords.map(c => c[1]));
        const maxLng = Math.max(...validCoords.map(c => c[0]));
        const maxLat = Math.max(...validCoords.map(c => c[1]));

        return [[minLng, minLat], [maxLng, maxLat]];
    };

    // Función para calcular el padding basado en el tamaño del área
    const getPaddingForBounds = (bounds) => {
        const [[minLng, minLat], [maxLng, maxLat]] = bounds;
        const lngDiff = maxLng - minLng;
        const latDiff = maxLat - minLat;

        // Si la diferencia es pequeña, aumentar el padding
        if (lngDiff < 0.005 && latDiff < 0.005) {
            return 150; // Aumentar padding para áreas pequeñas
        }
        return 50; // Padding estándar
    };

    // Función para hacer zoom a una capa cuando se activa su visibilidad
    const zoomToLayer = (layerId, geometry) => {
        if (!mapInstance.current || !geometry) return;

        const bounds = getBoundingBox(geometry);
        if (!bounds) {
            console.warn(`No se pudo calcular el bounding box de la capa: ${layerId}`);
            return;
        }

        mapInstance.current.fitBounds(bounds, {
            padding: getPaddingForBounds(bounds),
            maxZoom: 14, // Evita zoom excesivo en concesiones pequeñas
            duration: 1000, // Animación de zoom
        });
    };

    // Efecto para actualizar visibilidad de capas y hacer zoom
    useEffect(() => {
        toggleLayerVisibility('Concesión', showConcession);
        toggleLayerVisibility('Intesal', showBarrio);

        if (showConcession && itemConcession?.geometry) {
            zoomToLayer('Concesión', itemConcession.geometry);
        }
        if (showBarrio && itemConcession?.intesal_polygon) {
            zoomToLayer('Intesal', { type: "Polygon", coordinates: [itemConcession.intesal_polygon] });
        }
    }, [showConcession, showBarrio]);





    // Función para mostrar u ocultar capas
    const toggleLayerVisibility = (layerId, show) => {
        if (!mapInstance.current || !mapInstance.current.getLayer(layerId)) return;
        mapInstance.current.setLayoutProperty(layerId, 'visibility', show ? 'visible' : 'none');
        mapInstance.current.setLayoutProperty(`${layerId}-border`, 'visibility', show ? 'visible' : 'none');
    };

    // // Efecto para actualizar visibilidad de capas
    // useEffect(() => {
    //     toggleLayerVisibility('Concesión', showConcession);
    //     toggleLayerVisibility('Intesal', showBarrio);
    // }, [showConcession, showBarrio]);



    if (lat == null || lon == null) {
        return (
            <Card className='p-0 mb-2'>
                <CardHeader className='p-2 align-items-center d-flex'>
                    <h5 className="flex-grow-1 m-0">
                        <i className={`${Icons.pin} align-middle me-1 text-muted`}></i>{title}
                    </h5>
                </CardHeader>
                <CardBody className='p-2'>
                    <div className="d-flex justify-content-center align-items-center p-3">
                        <div className="text-center">
                            <div className="avatar-md">
                                <span className="avatar-title bg-info-subtle rounded-circle fs-1">
                                    <i className="ri-earth-fill text-info"></i>
                                </span>
                            </div>
                        </div>
                    </div> 
                </CardBody>
                <CardFooter className="m-0 p-2 text-center">
                    <h6 className="mb-0 p-0 text-wrap w-100">
                        <span className="fs-11 badge bg-info-subtle text-info text-wrap">
                        No cuenta con coordenadas
                        </span>
                    </h6>
                </CardFooter>
            </Card>
        );
    }

    return (
        <Card className='p-0 mb-2'>
            <CardHeader className='p-2 align-items-center d-flex'>
                <h5 className="flex-grow-1 m-0">
                    <i className={`${Icons.pin} align-middle me-1 text-muted`}></i>{title}
                </h5>
                <div className='btn-box-dashboard'>
                    {/* <div className='btn-box-dashboard'>
                    <Button
                        color="secondary"
                        size="sm"
                        className="btn-soft-secondary me-1"
                        onClick={() => setShowShips(!showShips)}
                    >
                        <i className="ri-ship-fill align-middle"></i>
                    </Button>

                    <Button
                        color="secondary"
                        size="sm"
                        className="btn-soft-secondary"
                        onClick={() => setShowPorts(!showPorts)}
                    >
                        <i className="ri-anchor-fill align-middle"></i>
                    </Button>
                </div> */}

                    {['SWE', 'LWE'].includes(code) && (
                        <>
                            <Button
                                color="secondary"
                                size="sm"
                                className="btn-soft-secondary me-1"
                                onClick={() => setShowConcession(!showConcession)}
                                active={showConcession}
                            >
                                Concesión
                            </Button>
                            <Button
                                color="secondary"
                                size="sm"
                                className="btn-soft-secondary me-1"
                                onClick={() => setShowBarrio(!showBarrio)}
                                active={showBarrio}
                            >
                                Intesal
                            </Button>
                        </>
                    )}
                </div>
            </CardHeader>
            <CardBody className='m-0 p-2'>
                {/* Contenedor del mapa */}
                <div ref={mapContainer} style={{ width: '100%', minHeight: height, height: height }}></div>

                {/* Leyenda de colores */}
                {itemRisksMaster && (
                    <div
                        style={{
                            position: 'absolute',
                            top: '50px',
                            right: '13px',
                            backgroundColor: 'rgba(255, 255, 255, 0.5)',
                            padding: '8px',
                            borderRadius: '5px',
                            boxShadow: '0 0 5px rgba(0,0,0,0.3)',
                            fontSize: '8px',
                            zIndex: 1
                        }}
                    >
                        <span className='fs-10 m-0 p-1'>Riesgo FAN</span>
                        {Object.entries(itemRisksMaster).map(([label, color]) => (
                            <div key={label} style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                                <div style={{ width: '12px', height: '12px', backgroundColor: color, marginRight: '8px' }}></div>
                                <span>{label}</span>
                            </div>
                        ))}
                    </div>
                )}
            </CardBody>
        </Card>
    );
};

export default MapWithLayers;
